import { DISPLACEMENT_CACHE_SIZE } from '@config/glass';

/**
 * Displacement maps for liquid glass refraction.
 *
 * A displacement map is an image whose red and green channels tell
 * `feDisplacementMap` where to sample from: 128 means "stay put", above or
 * below means "look this far right/left (red) or down/up (green)".
 *
 * Real glass with a rounded rim bends light at the rim and leaves the flat
 * middle alone, so the map is neutral grey everywhere except a band of
 * width `bezel` around the edge. Inside that band each pixel points inward,
 * along the surface normal, most strongly at the very edge. That inward
 * pull is what makes the backdrop appear to curve over the rim.
 *
 * Pointing inward is not only physically right, it is also what keeps the
 * filter clean: Chromium clips a backdrop to the element's box, so sampling
 * outward would read transparent pixels and darken the edge.
 */

export interface RimGeometry {
  /** Border-box size in CSS pixels. */
  readonly width: number;
  readonly height: number;
  /** Corner radius in CSS pixels; clamped to half the shorter side. */
  readonly radius: number;
  /** Width of the refracting rim in CSS pixels. */
  readonly bezel: number;
}

/** Channel value meaning "no displacement". */
const NEUTRAL = 128;
/** Furthest a channel can swing from neutral. */
const AMPLITUDE = 127;
/** RGBA(128, 128, 128, 255) packed little-endian, for a fast fill. */
const NEUTRAL_PIXEL = 0xff808080;

/** Least-recently-used: a hit is re-inserted so the oldest entry is first. */
const cache = new Map<string, string>();

/**
 * How strongly light bends at a point on the rim.
 *
 * `t` runs from 0 at the outer edge to 1 where the rim meets the flat
 * face. A squared falloff approximates the slope of a convex rim: steep at
 * the edge, flattening smoothly so there is no visible seam with the face.
 */
const bendAt = (t: number): number => {
  const s = 1 - t;
  return s * s;
};

/**
 * Writes one pixel's displacement, using the signed distance to a rounded
 * rectangle. Pixels in the flat face or outside a rounded corner are left
 * neutral.
 */
const writePixel = (
  data: Uint8ClampedArray,
  index: number,
  px: number,
  py: number,
  halfInnerX: number,
  halfInnerY: number,
  radius: number,
  bezel: number,
): void => {
  const ax = Math.abs(px) - halfInnerX;
  const ay = Math.abs(py) - halfInnerY;

  let depth: number;
  let nx: number;
  let ny: number;

  if (ax > 0 && ay > 0) {
    /* Corner arc: the normal points away from the arc's centre. */
    const length = Math.hypot(ax, ay);
    depth = radius - length;
    nx = (ax / length) * Math.sign(px);
    ny = (ay / length) * Math.sign(py);
  } else if (ax > ay) {
    depth = radius - ax;
    nx = Math.sign(px);
    ny = 0;
  } else {
    depth = radius - ay;
    nx = 0;
    ny = Math.sign(py);
  }

  if (depth < 0) return;
  const t = depth / bezel;
  if (t >= 1) return;

  const strength = bendAt(t) * AMPLITUDE;
  /* Negated normal: sample from further inside the shape. */
  data[index] = NEUTRAL - nx * strength;
  data[index + 1] = NEUTRAL - ny * strength;
};

/**
 * Renders the map for a surface and returns it as a PNG data URL, ready
 * for an `<feImage href>`.
 *
 * Only the rim band is computed. The face is a flat fill, so the cost scales
 * with the perimeter rather than the area: a full-width panel costs about
 * as much as a button.
 */
export const createDisplacementMap = (geometry: RimGeometry): string => {
  const width = Math.max(1, Math.round(geometry.width));
  const height = Math.max(1, Math.round(geometry.height));
  const radius = Math.min(geometry.radius, width / 2, height / 2);
  const bezel = Math.max(1, geometry.bezel);

  const key = `${String(width)}x${String(height)}r${String(radius)}b${String(bezel)}`;
  const cached = cache.get(key);
  if (cached !== undefined) {
    cache.delete(key);
    cache.set(key, cached);
    return cached;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return '';

  const image = context.createImageData(width, height);
  const { data } = image;
  new Uint32Array(data.buffer).fill(NEUTRAL_PIXEL);

  /* Past this distance from every edge nothing bends, including inside a
     corner whose radius is wider than the bezel. */
  const band = Math.min(Math.ceil(Math.max(radius, bezel)) + 1, Math.ceil(width / 2));
  const halfInnerX = width / 2 - radius;
  const halfInnerY = height / 2 - radius;

  for (let y = 0; y < height; y += 1) {
    const py = y + 0.5 - height / 2;
    const isEdgeRow = y < band || y >= height - band;

    for (let x = 0; x < width; x += 1) {
      /* Interior rows only need the left and right bands. The guard keeps
         the jump forward-only when the two bands meet on a narrow surface;
         jumping backwards would loop forever. */
      if (!isEdgeRow && x === band && width - band > band) x = width - band;
      writePixel(
        data,
        (y * width + x) * 4,
        x + 0.5 - width / 2,
        py,
        halfInnerX,
        halfInnerY,
        radius,
        bezel,
      );
    }
  }

  context.putImageData(image, 0, 0);
  const url = canvas.toDataURL('image/png');

  cache.set(key, url);
  if (cache.size > DISPLACEMENT_CACHE_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }

  return url;
};
